import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Image } from "@tiptap/extension-image";
import { Link } from "@tiptap/extension-link";
import { Underline } from "@tiptap/extension-underline";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import { Placeholder } from "@tiptap/extension-placeholder";
import { useCallback, useEffect, useRef } from "react";
import { uploadApi } from "../../services";
import toast from "../../utils/toast";
import "./tipTapEditor.css";

const PF = "https://backend.hoanglecf.com";

// ── Toolbar Button ──
function TBtn({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      className={`ttp-btn${active ? " active" : ""}${disabled ? " disabled" : ""}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

// ── Toolbar Separator ──
function TSep() {
  return <span className="ttp-sep" />;
}

// ── Toolbar ──
function Toolbar({ editor }) {
  const addImage = useCallback(() => {
    if (!editor) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        toast.warning("Ảnh không được vượt quá 10MB!");
        return;
      }
      try {
        const filename = Date.now() + file.name;
        const result = await uploadApi.uploadImage(file, filename);
        const url = PF + result.path;
        editor.chain().focus().setImage({ src: url }).run();
      } catch (err) {
        console.error("Upload image error:", err);
        toast.error("Tải ảnh lên thất bại!");
      }
    };
    input.click();
  }, [editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Nhập URL:", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addVideo = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Nhập URL YouTube hoặc Vimeo:");
    if (!url) return;
    let embedUrl = url;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (ytMatch) embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    editor
      .chain()
      .focus()
      .insertContent(
        `<div class="video-wrapper"><iframe src="${embedUrl}" frameborder="0" allowfullscreen></iframe></div>`
      )
      .run();
  }, [editor]);

  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="ttp-toolbar">
      {/* Text type */}
      <select
        className="ttp-select"
        value={
          editor.isActive("heading", { level: 1 }) ? "1"
          : editor.isActive("heading", { level: 2 }) ? "2"
          : editor.isActive("heading", { level: 3 }) ? "3"
          : editor.isActive("heading", { level: 4 }) ? "4"
          : "0"
        }
        onChange={(e) => {
          const v = parseInt(e.target.value);
          if (v === 0) editor.chain().focus().setParagraph().run();
          else editor.chain().focus().toggleHeading({ level: v }).run();
        }}
      >
        <option value="0">Normal</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
      </select>

      <TSep />

      {/* Inline */}
      <TBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
        <i className="fas fa-bold" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
        <i className="fas fa-italic" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline">
        <i className="fas fa-underline" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough">
        <i className="fas fa-strikethrough" />
      </TBtn>

      <TSep />

      {/* Color */}
      <label className="ttp-color" title="Text color">
        <i className="fas fa-font" />
        <input
          type="color"
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          value={editor.getAttributes("textStyle").color || "#ffffff"}
        />
      </label>
      <label className="ttp-color" title="Highlight">
        <i className="fas fa-highlighter" />
        <input
          type="color"
          onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
          value="#fef08a"
        />
      </label>

      <TSep />

      {/* Lists */}
      <TBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Ordered list">
        <i className="fas fa-list-ol" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list">
        <i className="fas fa-list-ul" />
      </TBtn>

      <TSep />

      {/* Alignment */}
      <TBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left">
        <i className="fas fa-align-left" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center">
        <i className="fas fa-align-center" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right">
        <i className="fas fa-align-right" />
      </TBtn>

      <TSep />

      {/* Block */}
      <TBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote">
        <i className="fas fa-quote-right" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block">
        <i className="fas fa-code" />
      </TBtn>

      <TSep />

      {/* Media */}
      <TBtn onClick={addLink} active={editor.isActive("link")} title="Link">
        <i className="fas fa-link" />
      </TBtn>
      <TBtn onClick={addImage} title="Upload image">
        <i className="fas fa-image" />
      </TBtn>
      <TBtn onClick={addVideo} title="Embed video">
        <i className="fas fa-video" />
      </TBtn>

      <TSep />

      {/* Table */}
      <TBtn onClick={insertTable} title="Insert table">
        <i className="fas fa-table" />
      </TBtn>
      {editor.isActive("table") && (
        <>
          <TBtn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column">
            <i className="fas fa-columns" />
          </TBtn>
          <TBtn onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row">
            <i className="fas fa-plus" />
          </TBtn>
          <TBtn onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete column">
            <i className="fas fa-minus-circle" style={{ color: "#ef4444" }} />
          </TBtn>
          <TBtn onClick={() => editor.chain().focus().deleteRow().run()} title="Delete row">
            <i className="fas fa-minus-square" style={{ color: "#ef4444" }} />
          </TBtn>
          <TBtn onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table">
            <i className="fas fa-trash" style={{ color: "#ef4444" }} />
          </TBtn>
        </>
      )}

      <TSep />

      {/* Undo/Redo */}
      <TBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <i className="fas fa-undo" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <i className="fas fa-redo" />
      </TBtn>

      <TBtn onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear formatting">
        <i className="fas fa-eraser" />
      </TBtn>
    </div>
  );
}

// ── Main Editor Component ──
export default function TipTapEditor({ content, onChange }) {
  const isInitialMount = useRef(true);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: "Nhập nội dung blog..." }),
    ],
    content: content || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update editor content when prop changes (e.g. loading existing blog)
  useEffect(() => {
    if (editor && content !== undefined) {
      // Only set content on initial load or when content changes externally
      if (isInitialMount.current) {
        isInitialMount.current = false;
        if (content && editor.getHTML() !== content) {
          editor.commands.setContent(content, false);
        }
      }
    }
  }, [editor, content]);

  if (!editor) return null;

  return (
    <div className="ttp-wrapper">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} className="ttp-content" />
    </div>
  );
}
